import { useEffect, useState } from "react";
import {
  Box, Paper, Button, Alert, TextField, InputAdornment,
  Dialog, DialogContent, DialogActions, CircularProgress,
  FormControlLabel, Switch, Grid2 as Grid,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { currenciesApi, type CurrencyDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

/**
 * Currency master. Charge rows quote a rate in one of these. Exactly one currency per tenant is
 * the base (reporting) currency — promoting another demotes the incumbent, and the base can be
 * neither deactivated nor deleted.
 */
export function CurrenciesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CurrencyDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("catalog.currency.manage"));

  const query = useQuery({
    queryKey: ["currencies", search, pagination.page, pagination.pageSize],
    queryFn: () => currenciesApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Currency deleted" },
    mutationFn: (id: string) => currenciesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["currencies"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: CurrencyDto) => { setEditing(row); setFormOpen(true); };
  const handleDelete = (row: CurrencyDto) => {
    if (confirm(`Delete currency "${row.code} — ${row.name}"? Blocked if any charge row is quoted in it.`)) {
      deleteMutation.mutate(row.id);
    }
  };

  const columns: GridColDef<CurrencyDto>[] = [
    { field: "code", headerName: "Code", width: 100 },
    { field: "name", headerName: "Currency", flex: 1, minWidth: 200 },
    { field: "symbol", headerName: "Symbol", width: 100, valueGetter: (_v, row) => row.symbol || "—" },
    { field: "decimalPlaces", headerName: "Decimals", width: 110, align: "right", headerAlign: "right" },
    {
      field: "isBaseCurrency", headerName: "Base", width: 120, sortable: false,
      renderCell: (p) => p.row.isBaseCurrency
        ? <StatusPill label="Reporting" tone="info" />
        : <Box component="span" sx={{ color: "text.disabled" }}>—</Box>,
    },
    {
      field: "isActive", headerName: "Status", width: 110, sortable: false,
      renderCell: (p) => <StatusPill label={p.row.isActive ? "Active" : "Inactive"} />,
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage, onClick: () => openEdit(p.row) },
          {
            label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage,
            disabledReason: p.row.isBaseCurrency ? "Base currency — cannot delete" : undefined,
            onClick: () => handleDelete(p.row),
          },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title="Currencies"
        subtitle="Currencies a service charge can be quoted in. One is the tenant's base reporting currency."
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New currency</Button>
        ) : undefined}
      />
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <CurrencyDialog open={formOpen} onClose={() => setFormOpen(false)} row={editing} />
    </Box>
  );
}

function CurrencyDialog({ open, onClose, row }: { open: boolean; onClose: () => void; row: CurrencyDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!row;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [isBaseCurrency, setIsBaseCurrency] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setCode(row?.code ?? "");
    setName(row?.name ?? "");
    setSymbol(row?.symbol ?? "");
    setDecimalPlaces(row?.decimalPlaces ?? 2);
    setIsBaseCurrency(row?.isBaseCurrency ?? false);
    setIsActive(row?.isActive ?? true);
  }, [open, row]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Currency updated" : "Currency created" },
    mutationFn: async () => {
      const payload = {
        code: code.trim().toUpperCase(), name: name.trim(),
        symbol: symbol.trim() || null,
        decimalPlaces, isBaseCurrency, isActive,
      };
      if (isEdit && row) { await currenciesApi.update(row.id, payload); return row.id; }
      const r = await currenciesApi.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["currencies"] });
      onClose();
    },
  });

  // The API rejects demoting or deactivating the base currency; keep the switches consistent here
  // so the user sees why rather than getting a 409 on save.
  const wasBase = row?.isBaseCurrency ?? false;
  const err =
    !/^[A-Za-z]{3}$/.test(code.trim()) ? "Code must be the 3-letter ISO-4217 code (INR, USD…)"
    : !name.trim() ? "Name required"
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit currency" : "New currency"}</ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Code *" size="small" fullWidth autoFocus
                value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                inputProps={{ maxLength: 3, style: { textTransform: "uppercase" } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Name *" size="small" fullWidth
                value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 100 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Symbol" size="small" fullWidth
                value={symbol} onChange={(e) => setSymbol(e.target.value)} inputProps={{ maxLength: 8 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Decimal places" size="small" fullWidth type="number"
                value={decimalPlaces}
                onChange={(e) => setDecimalPlaces(e.target.value === "" ? 0 : Number(e.target.value))}
                inputProps={{ min: 0, max: 4 }}
                helperText="0–4; rounds the billing screen" />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <FormControlLabel
                label="Base (reporting) currency"
                control={
                  <Switch checked={isBaseCurrency} disabled={wasBase}
                    onChange={(_, v) => { setIsBaseCurrency(v); if (v) setIsActive(true); }} />
                } />
              <FormControlLabel label="Active"
                control={
                  <Switch checked={isActive} disabled={isBaseCurrency}
                    onChange={(_, v) => setIsActive(v)} />
                } />
            </Grid>
          </Grid>
          {wasBase && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This is the base currency. Promote another currency to move the flag — a tenant must
              always have exactly one, and it cannot be deactivated.
            </Alert>
          )}
          {!wasBase && isBaseCurrency && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Saving will make this the base currency and demote the current one.
            </Alert>
          )}
          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
